/**
 * StockEntryDetailsSection — bespoke Thai-SME-friendly details card for
 * core Stock Entry metadata (type, posting date, company) with a
 * collapsible "advanced" group for naming series, add_to_transit, remarks.
 *
 * Read-only display; editing stays available in the "Additional Details"
 * tab which uses GenericSection with the raw ERPNext meta fields.
 *
 * Design language matches /settings → Appearance AND respects the user's
 * Detail Density + Detail Text Size preferences via `useDetailDensity()`.
 * Spacing, row padding, label size, and divider insets all scale with
 * the font-adjustment settings at /settings → Appearance.
 *
 *   - plain uppercase section header (density-aware text size)
 *   - CARD token from design-tokens.ts
 *   - Apple-style inset divider between rows (not full-bleed)
 *   - label uses muted-foreground; value uses foreground
 */

import { useState } from "react";
import { ChevronDown, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/i18n/useLanguage";
import { useDetailDensity, TEXT_LABEL, TEXT_SECTION_HEADER } from "../../../../hooks/useDetailDensity";
import {
  STOCK_ACTIONS,
  type StockActionConfig,
} from "@/features/common/doc-dashboard/stockActionsConfig";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";

const i18n: Record<string, { en: string; th: string }> = {
  details: { en: "Details", th: "รายละเอียด" },
  stock_entry_type: { en: "Movement type", th: "ประเภทการเคลื่อนไหว" },
  posting_date: { en: "Posted on", th: "วันที่บันทึก" },
  company: { en: "Company", th: "บริษัท" },
  advanced: { en: "Advanced options", th: "ตัวเลือกขั้นสูง" },
  naming_series: { en: "Naming series", th: "รหัสเอกสาร" },
  add_to_transit: { en: "In-transit stock", th: "สต็อกระหว่างขนส่ง" },
  remarks: { en: "Remarks", th: "หมายเหตุ" },
  enabled: { en: "Enabled", th: "เปิด" },
  disabled: { en: "Disabled", th: "ปิด" },
  no_remarks: { en: "No remarks", th: "ไม่มีหมายเหตุ" },
  not_set: { en: "—", th: "—" },
};

function formatDate(value: string | undefined, language: "en" | "th"): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(language === "th" ? "th-TH" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getActionConfig(stockEntryType: string | undefined): StockActionConfig | undefined {
  if (!stockEntryType) return undefined;
  return STOCK_ACTIONS.find(
    (a) => a.kind === "stock_entry_quick_action" && a.stockEntryType === stockEntryType,
  );
}

interface RowProps {
  label: string;
  children: React.ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  rowPx: string;
  rowPy: string;
  rowFirst: string;
  dividerMx: string;
  labelClass: string;
  valueClass: string;
}

function Row({
  label,
  children,
  isFirst,
  isLast,
  rowPx,
  rowPy,
  rowFirst,
  dividerMx,
  labelClass,
  valueClass,
}: RowProps) {
  return (
    <>
      <div
        className={`flex items-center justify-between gap-3 ${rowPx} ${
          isFirst ? rowFirst : rowPy
        }`}
      >
        <div className={`${labelClass} font-medium text-muted-foreground shrink-0`}>
          {label}
        </div>
        <div className={`${valueClass} text-foreground text-right min-w-0 truncate`}>
          {children}
        </div>
      </div>
      {!isLast ? <div className={`border-t border-border/40 ${dividerMx}`} /> : null}
    </>
  );
}

export function StockEntryDetailsSection({ formData }: ShopifySectionWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const { textSize, rowPx, rowPy, rowFirst, dividerMx } = useDetailDensity();
  const labelClass = TEXT_LABEL[textSize];
  const valueClass = TEXT_LABEL[textSize];
  const headerClass = TEXT_SECTION_HEADER[textSize];

  const [advancedOpen, setAdvancedOpen] = useState(false);

  const stockEntryType = (formData.stock_entry_type as string) || "";
  const postingDate = (formData.posting_date as string) || "";
  const company = (formData.company as string) || "";
  const namingSeries = (formData.naming_series as string) || "";
  const addToTransit = Boolean(formData.add_to_transit);
  const remarks = (formData.remarks as string) || "";

  const action = getActionConfig(stockEntryType);
  const TypeIcon = action?.icon;

  // Only show add_to_transit row for Material Transfer (the only type that supports it)
  const showTransitRow = stockEntryType === "Material Transfer";

  // Build the advanced row list dynamically so we know which one is last
  // (needed to suppress the trailing divider on the final row — Apple style).
  const advancedRows = [
    { key: "naming_series", label: t("naming_series") },
    ...(showTransitRow ? [{ key: "add_to_transit", label: t("add_to_transit") }] : []),
    { key: "remarks", label: t("remarks") },
  ];

  return (
    <div className="space-y-2">
      <h3
        className={`${headerClass} font-semibold text-muted-foreground uppercase tracking-wide pl-4`}
      >
        {t("details")}
      </h3>
      <Card className={CARD}>
        <CardContent className="p-0">
          {/* Main rows — always visible */}
          <Row
            label={t("stock_entry_type")}
            isFirst
            rowPx={rowPx}
            rowPy={rowPy}
            rowFirst={rowFirst}
            dividerMx={dividerMx}
            labelClass={labelClass}
            valueClass={valueClass}
          >
            <div className="flex items-center justify-end gap-2 min-w-0">
              {TypeIcon ? (
                <TypeIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : null}
              <span className="truncate">{stockEntryType || t("not_set")}</span>
            </div>
          </Row>
          <Row
            label={t("posting_date")}
            rowPx={rowPx}
            rowPy={rowPy}
            rowFirst={rowFirst}
            dividerMx={dividerMx}
            labelClass={labelClass}
            valueClass={valueClass}
          >
            {formatDate(postingDate, language === "th" ? "th" : "en")}
          </Row>
          <Row
            label={t("company")}
            isLast={!advancedOpen}
            rowPx={rowPx}
            rowPy={rowPy}
            rowFirst={rowFirst}
            dividerMx={dividerMx}
            labelClass={labelClass}
            valueClass={valueClass}
          >
            <div className="flex items-center justify-end gap-2 min-w-0">
              <span className="truncate">{company || t("not_set")}</span>
              <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            </div>
          </Row>

          {/* Collapsible advanced options */}
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className={`w-full flex items-center justify-between gap-2 ${rowPx} ${rowPy} ${labelClass} font-medium text-muted-foreground hover:text-foreground transition-colors`}
            aria-expanded={advancedOpen}
          >
            <span>{t("advanced")}</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
            />
          </button>

          {advancedOpen ? (
            <div>
              <div className={`border-t border-border/40 ${dividerMx}`} />
              {advancedRows.map((row, idx) => {
                const isLastRow = idx === advancedRows.length - 1;
                if (row.key === "naming_series") {
                  return (
                    <Row
                      key={row.key}
                      label={row.label}
                      isFirst
                      isLast={isLastRow}
                      rowPx={rowPx}
                      rowPy={rowPy}
                      rowFirst={rowFirst}
                      dividerMx={dividerMx}
                      labelClass={labelClass}
                      valueClass={valueClass}
                    >
                      <span className="font-mono text-xs">{namingSeries || t("not_set")}</span>
                    </Row>
                  );
                }
                if (row.key === "add_to_transit") {
                  return (
                    <Row
                      key={row.key}
                      label={row.label}
                      isLast={isLastRow}
                      rowPx={rowPx}
                      rowPy={rowPy}
                      rowFirst={rowFirst}
                      dividerMx={dividerMx}
                      labelClass={labelClass}
                      valueClass={valueClass}
                    >
                      <span
                        className={
                          addToTransit
                            ? "text-emerald-700 dark:text-emerald-300"
                            : "text-muted-foreground"
                        }
                      >
                        {addToTransit ? t("enabled") : t("disabled")}
                      </span>
                    </Row>
                  );
                }
                return (
                  <Row
                    key={row.key}
                    label={row.label}
                    isLast={isLastRow}
                    rowPx={rowPx}
                    rowPy={rowPy}
                    rowFirst={rowFirst}
                    dividerMx={dividerMx}
                    labelClass={labelClass}
                    valueClass={valueClass}
                  >
                    <span className={remarks ? "" : "text-muted-foreground italic"}>
                      {remarks || t("no_remarks")}
                    </span>
                  </Row>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
